import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, Mail, Save, Unlink } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import AutoSendContractsToggle from "@/components/settings/AutoSendContractsToggle";
import TrialLocked from "@/components/trial/TrialLocked";
import { useTrialAccount } from "@/hooks/useTrialAccount";
import { ORGANIZATION_SETTINGS_QUERY_KEY } from "@/hooks/queries";
import {
  getOrganizationSettingsErrorMessage,
  organizationSettingsService,
  type ContractEmailOAuthProvider,
  type OrganizationSettings,
  type UpdateOrganizationSettingsRequest,
} from "@/services/organizationSettings";

interface OrganizationSectionProps {
  settings: OrganizationSettings | undefined;
  loading: boolean;
}

/** "10.00" -> "10", "12.50" -> "12.5"; falls back to the platform default of 10. */
export const formatCommissionPct = (raw: string | number | null | undefined): string => {
  if (raw == null || raw === "") return "10";
  const n = Number(raw);
  if (!Number.isFinite(n)) return "10";
  const rounded = Math.round(n * 100) / 100;
  return String(rounded);
};

const OrganizationSection: React.FC<OrganizationSectionProps> = ({ settings, loading }) => {
  const { isTrial } = useTrialAccount();
  const queryClient = useQueryClient();
  const [commissionInput, setCommissionInput] = useState("");
  const [refiCommissionInput, setRefiCommissionInput] = useState("");
  const [commissionError, setCommissionError] = useState<string | null>(null);
  const [commissionSaved, setCommissionSaved] = useState(false);
  const [inboxError, setInboxError] = useState<string | null>(null);
  const [sendPrefsError, setSendPrefsError] = useState<string | null>(null);
  const commissionHydratedRef = useRef(false);

  useEffect(() => {
    if (!settings || commissionHydratedRef.current) return;
    commissionHydratedRef.current = true;
    setCommissionInput(formatCommissionPct(settings.default_commission_percentage));
    setRefiCommissionInput(formatCommissionPct(settings.default_refi_commission_percentage));
  }, [settings]);

  const commissionMutation = useMutation({
    mutationFn: (payload: {
      default_commission_percentage: string;
      default_refi_commission_percentage: string;
    }) => organizationSettingsService.update(payload),
    onMutate: () => {
      setCommissionError(null);
      setCommissionSaved(false);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(ORGANIZATION_SETTINGS_QUERY_KEY, data);
      setCommissionInput(formatCommissionPct(data.default_commission_percentage));
      setRefiCommissionInput(formatCommissionPct(data.default_refi_commission_percentage));
      setCommissionSaved(true);
      window.setTimeout(() => setCommissionSaved(false), 2000);
    },
    onError: (err) => setCommissionError(getOrganizationSettingsErrorMessage(err)),
  });

  const parsedCommission = Number(commissionInput);
  const parsedRefiCommission = Number(refiCommissionInput);
  const pctValid = (raw: string, n: number) =>
    raw.trim() !== "" && Number.isFinite(n) && n >= 0 && n <= 25;
  const commissionValid =
    pctValid(commissionInput, parsedCommission) &&
    pctValid(refiCommissionInput, parsedRefiCommission);
  const commissionDirty =
    commissionInput.trim() !== formatCommissionPct(settings?.default_commission_percentage) ||
    refiCommissionInput.trim() !==
      formatCommissionPct(settings?.default_refi_commission_percentage);

  const saveCommission = () => {
    if (!commissionValid) {
      setCommissionError("Enter a percentage between 0 and 25 for each field.");
      return;
    }
    commissionMutation.mutate({
      default_commission_percentage: String(Math.round(parsedCommission * 100) / 100),
      default_refi_commission_percentage: String(Math.round(parsedRefiCommission * 100) / 100),
    });
  };

  const connectInbox = useMutation({
    mutationFn: (provider: ContractEmailOAuthProvider) =>
      organizationSettingsService.startSubmissionsInboxOAuth(provider),
    onMutate: () => setInboxError(null),
    onSuccess: (data) => {
      window.location.href = data.authorization_url;
    },
    onError: (err) => setInboxError(getOrganizationSettingsErrorMessage(err)),
  });

  const inboxPrefs = useMutation({
    mutationFn: (payload: UpdateOrganizationSettingsRequest) =>
      organizationSettingsService.update(payload),
    onMutate: () => setInboxError(null),
    onSuccess: (data) => {
      queryClient.setQueryData(ORGANIZATION_SETTINGS_QUERY_KEY, data);
    },
    onError: (err) => setInboxError(getOrganizationSettingsErrorMessage(err)),
  });

  const disconnectInbox = useMutation({
    mutationFn: () => organizationSettingsService.disconnectSubmissionsInbox(),
    onMutate: () => setInboxError(null),
    onSuccess: (data) => {
      queryClient.setQueryData(ORGANIZATION_SETTINGS_QUERY_KEY, data);
    },
    onError: (err) => setInboxError(getOrganizationSettingsErrorMessage(err)),
  });

  const sendPrefs = useMutation({
    mutationFn: (payload: UpdateOrganizationSettingsRequest) =>
      organizationSettingsService.update(payload),
    onMutate: () => setSendPrefsError(null),
    onSuccess: (data) => {
      queryClient.setQueryData(ORGANIZATION_SETTINGS_QUERY_KEY, data);
    },
    onError: (err) => setSendPrefsError(getOrganizationSettingsErrorMessage(err)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Organization Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3 rounded-md border p-3" data-testid="submissions-inbox">
          <div className="space-y-0.5">
            <Label>Submissions inbox</Label>
            <p className="text-xs text-muted-foreground">
              {isTrial
                ? "Inbox ingestion is turned off for trial accounts."
                : "Connect the mailbox where brokers send applications."}
            </p>
          </div>
          {inboxError && (
            <Alert variant="destructive">
              <AlertDescription>{inboxError}</AlertDescription>
            </Alert>
          )}
          {settings?.submissions_inbox_reconnect_required && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This inbox can't be read anymore. Reconnect it to keep ingesting new mail.
              </AlertDescription>
            </Alert>
          )}
          {loading ? (
            <p className="text-xs text-muted-foreground">Loading...</p>
          ) : settings?.submissions_inbox_connected ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm">
                Connected as{" "}
                <span className="font-medium text-foreground">
                  {settings.submissions_inbox_address}
                </span>{" "}
                ({settings.submissions_inbox_provider === "outlook" ? "Outlook" : "Gmail"})
              </p>
              <TrialLocked lock="inbox" inline>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => disconnectInbox.mutate()}
                  disabled={disconnectInbox.isPending}
                >
                  <Unlink className="h-4 w-4" />
                  {disconnectInbox.isPending ? "Disconnecting..." : "Disconnect"}
                </Button>
              </TrialLocked>
            </div>
          ) : (
            <TrialLocked lock="inbox" inline>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => connectInbox.mutate("google")}
                  disabled={connectInbox.isPending}
                >
                  {connectInbox.isPending && connectInbox.variables === "google" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4" />
                  )}
                  Connect Gmail
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => connectInbox.mutate("microsoft")}
                  disabled={connectInbox.isPending}
                >
                  {connectInbox.isPending && connectInbox.variables === "microsoft" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4" />
                  )}
                  Connect Outlook
                </Button>
              </div>
            </TrialLocked>
          )}
          <div className="flex items-center justify-between gap-3">
            <div className="space-y-0.5">
              <Label htmlFor="submissions_inbox_label_mail">Label mail</Label>
              <p className="text-xs text-muted-foreground">
                Tag each conversation as Processing, Ready, Funded, or Declined.
              </p>
            </div>
            <Switch
              id="submissions_inbox_label_mail"
              checked={settings?.submissions_inbox_label_mail ?? true}
              disabled={loading || inboxPrefs.isPending}
              onCheckedChange={(checked) =>
                inboxPrefs.mutate({ submissions_inbox_label_mail: checked })
              }
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="space-y-0.5">
              <Label htmlFor="submissions_inbox_mark_read">Mark mail as read</Label>
              <p className="text-xs text-muted-foreground">
                Leave new mail unread unless this is on. Turning it on does not go back through mail
                already handled.
              </p>
            </div>
            <Switch
              id="submissions_inbox_mark_read"
              checked={settings?.submissions_inbox_mark_read ?? false}
              disabled={loading || inboxPrefs.isPending}
              onCheckedChange={(checked) =>
                inboxPrefs.mutate({ submissions_inbox_mark_read: checked })
              }
            />
          </div>
        </div>

        <div className="space-y-2 rounded-md border p-3" data-testid="default-commission">
          {commissionError && (
            <Alert variant="destructive">
              <AlertDescription>{commissionError}</AlertDescription>
            </Alert>
          )}
          <Label>Commission</Label>
          <p className="text-xs text-muted-foreground">
            Starting commission % for new contracts and refinance offers when a deal has no saved
            percentage yet.
          </p>
          <TrialLocked lock="commission" inline>
            <div className="space-y-3">
              <div className="flex flex-wrap items-start gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="default_commission_percentage">Default commission</Label>
                  <div className="relative w-28">
                    <Input
                      id="default_commission_percentage"
                      type="number"
                      inputMode="decimal"
                      min={0}
                      max={25}
                      step={0.5}
                      value={commissionInput}
                      onChange={(e) => setCommissionInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveCommission();
                      }}
                      disabled={loading || commissionMutation.isPending}
                      className="pr-7"
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
                      %
                    </span>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="default_refi_commission_percentage">Refi commission</Label>
                  <div className="relative w-28">
                    <Input
                      id="default_refi_commission_percentage"
                      type="number"
                      inputMode="decimal"
                      min={0}
                      max={25}
                      step={0.5}
                      value={refiCommissionInput}
                      onChange={(e) => setRefiCommissionInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveCommission();
                      }}
                      disabled={loading || commissionMutation.isPending}
                      className="pr-7"
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
                      %
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={saveCommission}
                  disabled={
                    loading || commissionMutation.isPending || !commissionDirty || !commissionValid
                  }
                >
                  {commissionMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {commissionMutation.isPending ? "Saving..." : "Save"}
                </Button>
                {commissionSaved && <span className="text-xs text-muted-foreground">Saved.</span>}
              </div>
            </div>
          </TrialLocked>
        </div>

        <div className="space-y-4 rounded-md border p-3" data-testid="merchant-send-controls">
          {sendPrefsError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{sendPrefsError}</AlertDescription>
            </Alert>
          )}
          <AutoSendContractsToggle
            checked={settings?.auto_send_contracts_enabled ?? true}
            disabled={loading || sendPrefs.isPending}
            onCheckedChange={(checked) =>
              sendPrefs.mutate({ auto_send_contracts_enabled: checked })
            }
          />
          <div className="flex items-center justify-between gap-4 border-t pt-4">
            <div className="space-y-0.5 pr-4">
              <Label htmlFor="underwriters_can_send_merchant_tasks">
                Underwriters can send Merchant Tasks
              </Label>
              <p className="text-xs text-muted-foreground">
                When off, underwriters can still review deals but cannot start or send Merchant
                Tasks.
              </p>
            </div>
            <Switch
              id="underwriters_can_send_merchant_tasks"
              checked={settings?.underwriters_can_send_merchant_tasks ?? true}
              disabled={loading || sendPrefs.isPending}
              onCheckedChange={(checked) =>
                sendPrefs.mutate({ underwriters_can_send_merchant_tasks: checked })
              }
              aria-label="Underwriters can send Merchant Tasks"
            />
          </div>
          <div className="flex items-center justify-between gap-4 border-t pt-4">
            <div className="space-y-0.5 pr-4">
              <Label htmlFor="brokers_can_send_merchant_tasks">
                Brokers can send Merchant Tasks
              </Label>
              <p className="text-xs text-muted-foreground">
                When off, brokers can still manage applications but cannot start or send Merchant
                Tasks.
              </p>
            </div>
            <Switch
              id="brokers_can_send_merchant_tasks"
              checked={settings?.brokers_can_send_merchant_tasks ?? true}
              disabled={loading || sendPrefs.isPending}
              onCheckedChange={(checked) =>
                sendPrefs.mutate({ brokers_can_send_merchant_tasks: checked })
              }
              aria-label="Brokers can send Merchant Tasks"
            />
          </div>
          <div className="flex items-center justify-between gap-4 border-t pt-4">
            <div className="space-y-0.5 pr-4">
              <Label htmlFor="disclosure_warning_enabled">Pre-engagement disclosure warning</Label>
              <p className="text-xs text-muted-foreground">
                Warn before a send that leaves out the pre-engagement disclosure in a state that
                requires one. Turn off to send without this warning.
              </p>
            </div>
            <TrialLocked lock="disclosureWarning" inline side="left">
              <Switch
                id="disclosure_warning_enabled"
                checked={settings?.disclosure_warning_enabled ?? true}
                disabled={loading || sendPrefs.isPending}
                onCheckedChange={(checked) =>
                  sendPrefs.mutate({ disclosure_warning_enabled: checked })
                }
                aria-label="Pre-engagement disclosure warning"
              />
            </TrialLocked>
          </div>
          <div className="flex items-center justify-between gap-4 border-t pt-4">
            <div className="space-y-0.5 pr-4">
              <Label htmlFor="offer_term_always_days">
                Offer calculator term is always in days
              </Label>
              <p className="text-xs text-muted-foreground">
                When on, Term is always in days. When off, the Term value matches the frequency
                (days for daily, weeks for weekly, months for monthly).
              </p>
            </div>
            <Switch
              id="offer_term_always_days"
              checked={settings?.offer_term_always_days ?? true}
              disabled={loading || sendPrefs.isPending}
              onCheckedChange={(checked) => sendPrefs.mutate({ offer_term_always_days: checked })}
              aria-label="Offer calculator term is always in days"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default OrganizationSection;
